import { useCallback, useContext, useMemo, useState } from 'react'
import { AppDataContext } from '../../../providers/AppData'
import useTranslateMessage from '../../../hooks/useTranslateMessage'
import { SearchTypes } from '../../../providers/SearchProfilesPage/types'
import { TENANTS } from '../../../constants/global'
import { SearchProfilesPageContext } from '../../../providers/SearchProfilesPage'
import { FILTER_OPTIONS, TEST_IDS } from '../../../__tests__/testUtils/constants'
import getDistancesForLocale from '../../../utils/getDistancesForLocale'
import { useGetUnitsByLocale } from '../../../hooks/useGetUnitsByLocale'
import { SearchFiltersProps } from '../../SearchPage/Default/SearchBar/SearchFilters'
import { useTrackFiltersValues } from '../../../hooks/useSendAnalytic'
import { useFeatureFlags } from '../../../hooks/useFeatureFlags'
import { newSearchTypesPractices } from '../../../constants/global'
import { GoldMedalIcon, GoldPremiumIcon, GreenCircleIcon, PramIcon, SvgIcon } from '@hermes/web-components'

export enum FilterType {
  checkbox,
  dropdown,
  button
}

export type CheckboxFilterType = {
  type: FilterType.checkbox
  Icon: typeof SvgIcon
  showingCondition: boolean
  name: string
  testId: string
  label: string
  value: boolean
  onChangeFilter: () => {}
}

export type DropdownFilterType = {
  type: FilterType.dropdown
  name: string
  testId?: string
  label: string
  value: string | number | readonly string[] | undefined
  resetOption: { label: string; value: string | number | undefined }
  options: Array<{ label: string; value: string | number | undefined }>
  onChangeFilter: (value?: string) => {}
}

export type ButtonFilterType = {
  type: FilterType.button
  testId: string
  label: string
  onClick: () => {}
}

const useSearchFiltersConfig = ({ insurers, languages, specialties }: SearchFiltersProps) => {
  const { locale, language } = useContext(AppDataContext)
  const {
    changeFilter,
    changeTypeOfSpecialist,
    state: { filters, specialty }
  } = useContext(SearchProfilesPageContext)
  const translate = useTranslateMessage()
  const { alternativeSearchEnabled } = useFeatureFlags()

  const labelAll = useMemo(() => translate('all'), [translate])

  const isSpecialistSearch = useMemo(
    () => filters.searchType === SearchTypes.specialists || filters.searchType === SearchTypes.healthAndSocialCare,
    [filters.searchType]
  )

  const isAlternativeSearchTypesIncluded = useMemo(
    () => filters?.searchType && newSearchTypesPractices.includes(filters?.searchType),
    [filters?.searchType]
  )

  const getUnitsByLocale = useGetUnitsByLocale()
  const filterTrackValues = useTrackFiltersValues()

  const [showMapModal, setShowMapModal] = useState(false)

  const toggleMapModal = useCallback(() => {
    setShowMapModal((prev: boolean) => !prev)
    return window.dataLayer.push({
      event: 'src_pg_view_map',
      src_keyword: specialty?.name?.[language] || '',
      src_location: filters?.location || ''
    })
  }, [filters?.location, language, specialty?.name])

  const distancesForLocale = getDistancesForLocale()
  const mappedArrayOfDistance = useMemo(
    () =>
      distancesForLocale
        .filter((x) => !!x)
        .map((val) => ({
          label: val === 'nolimit' ? translate('no_limit') : getUnitsByLocale(val).distanceWithUnits,
          value: val === 'nolimit' ? 'nolimit' : val
        })),
    [getUnitsByLocale, translate, distancesForLocale]
  )

  const typeOfSpecialist = useMemo(
    () => (isSpecialistSearch || alternativeSearchEnabled ? specialties : []),
    [isSpecialistSearch, specialties, alternativeSearchEnabled]
  )

  const allFilters = useMemo(() => {
    // potential to extend
    // eslint-disable-next-line sonarjs/no-small-switch
    switch (locale) {
      case TENANTS.WPA: {
        return [
          {
            type: FilterType.checkbox,
            showingCondition: !isSpecialistSearch,
            name: FILTER_OPTIONS.PREMIUM,
            Icon: GoldPremiumIcon,
            testId: `${TEST_IDS.SEARCH_FILTER}-${FILTER_OPTIONS.PREMIUM}`,
            label: translate('search.filter.premium'),
            value: filters.premium,
            onChangeFilter: () => changeFilter(FILTER_OPTIONS.PREMIUM, !filters.premium)
          },
          {
            type: FilterType.checkbox,
            Icon: GoldMedalIcon,
            showingCondition: isSpecialistSearch,
            name: FILTER_OPTIONS.FEE_AGREED,
            testId: `${TEST_IDS.SEARCH_FILTER}-${FILTER_OPTIONS.FEE_AGREED}`,
            label: translate('search.filter.fee_agreed'),
            value: filters.feeAgreed,
            onChangeFilter: () => changeFilter(FILTER_OPTIONS.FEE_AGREED, !filters.feeAgreed)
          },
          {
            type: FilterType.checkbox,
            Icon: PramIcon,
            showingCondition: isSpecialistSearch,
            name: FILTER_OPTIONS.SEES_CHILDREN,
            testId: `${TEST_IDS.SEARCH_FILTER}-${FILTER_OPTIONS.PATIENT_CHILDREN}`,
            label: translate('search.filter.sees_children'),
            value: filters.patientsChildren,
            onChangeFilter: () => changeFilter(FILTER_OPTIONS.PATIENT_CHILDREN, !filters.patientsChildren)
          },
          {
            type: FilterType.checkbox,
            Icon: GreenCircleIcon,
            showingCondition: isSpecialistSearch,
            name: FILTER_OPTIONS.SLOTS,
            testId: `${TEST_IDS.SEARCH_FILTER}-${FILTER_OPTIONS.SLOTS}`,
            label: translate('search.filter.slots_available'),
            value: filters.slots,
            onChangeFilter: () => changeFilter(FILTER_OPTIONS.SLOTS, !filters.slots)
          },
          {
            type: FilterType.dropdown,
            name: 'specialties',
            showingCondition: isSpecialistSearch,
            testId: `${TEST_IDS.SEARCH_FILTER}-${FILTER_OPTIONS.SPECIALTIES}`,
            label: translate('search.filter.type_of_specialist'),
            resetOption: { label: labelAll, value: '' },
            options: typeOfSpecialist.map((item) => ({ label: item.name[language], value: item.id.toString() })),
            value: filters.specialties[1] || '',
            onChangeFilter: (value: string) => {
              changeTypeOfSpecialist(value)
              const type = typeOfSpecialist.find((ts) => ts.id.toString() === value)?.name?.[language]
              window.dataLayer.push({
                event: 'src_pg_filter_change_sp_type',
                ...filterTrackValues,
                fltr_sp_type: type || 'All'
              })
            }
          },
          {
            type: FilterType.dropdown,
            name: 'distance',
            showingCondition: true,
            testId: `${TEST_IDS.SEARCH_FILTER}-${FILTER_OPTIONS.DISTANCE}`,
            label: translate('search.filter.distance'),
            options: mappedArrayOfDistance,
            value: mappedArrayOfDistance.map((x) => x.value)?.includes(filters?.distance?.toString())
              ? filters?.distance?.toString()
              : 'nolimit',
            onChangeFilter: (value: string) => {
              changeFilter('distance', value)
              window.dataLayer.push({
                event: 'src_pg_filter_change_distance',
                ...filterTrackValues,
                fltr_src_distance: value || '25'
              })
            }
          },
          {
            type: FilterType.dropdown,
            name: 'gender',
            showingCondition: isSpecialistSearch,
            testId: `${TEST_IDS.SEARCH_FILTER}-${FILTER_OPTIONS.GENDER}`,
            label: translate('search.filter.gender'),
            resetOption: { label: labelAll, value: '' },
            options: ['male', 'female', 'other'].map((gender) => ({ label: translate(gender), value: gender })),
            value: filters.gender,
            onChangeFilter: (value: string) => {
              changeFilter('gender', value)
              window.dataLayer.push({
                event: 'src_pg_filter_change_gender',
                ...filterTrackValues,
                fltr_gender: value || 'All'
              })
            }
          },
          {
            type: FilterType.dropdown,
            name: 'language',
            showingCondition: isSpecialistSearch,
            testId: `${TEST_IDS.SEARCH_FILTER}-${FILTER_OPTIONS.LANGUAGE}`,
            label: translate('search.filter.language'),
            resetOption: { label: labelAll, value: '' },
            options: languages.map((item) => ({ label: item.name, value: item.id })),
            value: filters.language,
            onChangeFilter: (value: string) => {
              changeFilter('language', value)
              window.dataLayer.push({
                event: 'src_pg_filter_change_lang',
                ...filterTrackValues,
                fltr_language: value || 'All'
              })
            }
          },
          {
            type: FilterType.button,
            showingCondition: true,
            testId: TEST_IDS.SEARCH_FILTER_MAP_BUTTON,
            label: translate('search.view_map'),
            onClick: () => toggleMapModal()
          }
        ]
      }
      default: {
        return [
          {
            type: FilterType.checkbox,
            showingCondition: isSpecialistSearch,
            name: FILTER_OPTIONS.SEES_CHILDREN,
            testId: `${TEST_IDS.SEARCH_FILTER}-${FILTER_OPTIONS.PATIENT_CHILDREN}`,
            label: translate('search.filter.sees_children'),
            value: filters.patientsChildren,
            onChangeFilter: () => changeFilter(FILTER_OPTIONS.PATIENT_CHILDREN, !filters.patientsChildren)
          },
          {
            type: FilterType.dropdown,
            name: 'specialties',
            showingCondition: isSpecialistSearch,
            testId: `${TEST_IDS.SEARCH_FILTER}-${FILTER_OPTIONS.SPECIALTIES}`,
            label: translate('search.filter.type_of_specialist'),
            resetOption: { label: labelAll, value: '' },
            options: typeOfSpecialist.map((item) => ({ label: item.name[language], value: item.id.toString() })),
            value: filters.specialties[1] || '',
            onChangeFilter: (value: string) => {
              changeTypeOfSpecialist(value)
              const type = typeOfSpecialist.find((ts) => ts.id.toString() === value)?.name?.[language]
              window.dataLayer.push({
                event: 'src_pg_filter_change_sp_type',
                ...filterTrackValues,
                fltr_sp_type: type || 'All'
              })
            }
          },
          {
            type: FilterType.dropdown,
            name: 'distance',
            showingCondition: true,
            testId: `${TEST_IDS.SEARCH_FILTER}-${FILTER_OPTIONS.DISTANCE}`,
            label: translate('search.filter.distance'),
            options: mappedArrayOfDistance,
            value: mappedArrayOfDistance.map((x) => x.value)?.includes(filters?.distance?.toString())
              ? filters?.distance?.toString()
              : 'nolimit',
            onChangeFilter: (value: string) => {
              changeFilter('distance', value)
              window.dataLayer.push({
                event: 'src_pg_filter_change_distance',
                ...filterTrackValues,
                fltr_src_distance: value || '25'
              })
            }
          },
          {
            type: FilterType.dropdown,
            name: 'specialties',
            showingCondition: alternativeSearchEnabled && isAlternativeSearchTypesIncluded,
            testId: `${TEST_IDS.SEARCH_FILTER}-${FILTER_OPTIONS.SPECIALTIES}`,
            label: translate('specialty'),
            resetOption: { label: labelAll, value: '' },
            options: typeOfSpecialist.map((item) => ({ label: item.name[language], value: item.id.toString() })),
            value: filters.specialties[0] || '',
            onChangeFilter: (value: string) => {
              changeTypeOfSpecialist(value)
              changeFilter('specialties', [value])
              const type = typeOfSpecialist.find((ts) => ts.id.toString() === value)?.name?.[language]
              window.dataLayer.push({
                event: 'src_pg_filter_change_sp_type',
                ...filterTrackValues,
                fltr_sp_type: type || 'All'
              })
            }
          },
          {
            type: FilterType.dropdown,
            name: 'appointmentType',
            showingCondition: isSpecialistSearch,
            testId: `${TEST_IDS.SEARCH_FILTER}-${FILTER_OPTIONS.TYPE_OF_CONSULTATION}`,
            label: translate('search.filter.type_of_consultation'),
            resetOption: { label: labelAll, value: '' },
            options: [
              { label: translate('video_consultation'), value: 'videoConsultation' },
              { label: translate('in_person'), value: 'inPerson' }
            ],
            value: filters.appointmentType,
            onChangeFilter: (value: string) => {
              changeFilter('appointmentType', value)
              window.dataLayer.push({
                event: 'src_pg_filter_change_appt_type',
                ...filterTrackValues,
                fltr_appointment_type: value || 'All'
              })
            }
          },
          {
            type: FilterType.dropdown,
            name: 'gender',
            showingCondition: isSpecialistSearch,
            testId: `${TEST_IDS.SEARCH_FILTER}-${FILTER_OPTIONS.GENDER}`,
            label: translate('search.filter.gender'),
            resetOption: { label: labelAll, value: '' },
            options: ['male', 'female', 'other'].map((gender) => ({ label: translate(gender), value: gender })),
            value: filters.gender,
            onChangeFilter: (value: string) => {
              changeFilter('gender', value)
              window.dataLayer.push({
                event: 'src_pg_filter_change_gender',
                ...filterTrackValues,
                fltr_gender: value || 'All'
              })
            }
          },
          {
            type: FilterType.dropdown,
            name: 'language',
            showingCondition: isSpecialistSearch,
            testId: `${TEST_IDS.SEARCH_FILTER}-${FILTER_OPTIONS.LANGUAGE}`,
            label: translate('search.filter.language'),
            resetOption: { label: labelAll, value: '' },
            options: languages.map((item) => ({ label: item.name, value: item.id })),
            value: filters.language,
            onChangeFilter: (value: string) => {
              changeFilter('language', value)
              window.dataLayer.push({
                event: 'src_pg_filter_change_lang',
                ...filterTrackValues,
                fltr_language: value || 'All'
              })
            }
          },
          {
            type: FilterType.dropdown,
            name: 'insurer',
            showingCondition: true,
            testId: `${TEST_IDS.SEARCH_FILTER}-${FILTER_OPTIONS.INSURANCE}`,
            label: translate('search.filter.insurance'),
            resetOption: { label: labelAll, value: '' },
            options: insurers.map((item) => ({ label: item.name, value: item.id })),
            value: filters.insurer,
            onChangeFilter: (value: string) => {
              changeFilter('insurer', value)
              window.dataLayer.push({
                event: 'src_pg_filter_change_insure',
                ...filterTrackValues,
                fltr_insurance: value || 'All'
              })
            }
          },
          {
            type: FilterType.button,
            showingCondition: true,
            testId: TEST_IDS.SEARCH_FILTER_MAP_BUTTON,
            label: translate('search.view_map'),
            onClick: () => toggleMapModal()
          }
        ]
      }
    }
  }, [
    locale,
    isSpecialistSearch,
    translate,
    filters.premium,
    filters.feeAgreed,
    filters.patientsChildren,
    filters.slots,
    filters.specialties,
    filters?.distance,
    filters.gender,
    filters.language,
    filters.appointmentType,
    filters.insurer,
    labelAll,
    typeOfSpecialist,
    mappedArrayOfDistance,
    languages,
    changeFilter,
    language,
    changeTypeOfSpecialist,
    filterTrackValues,
    toggleMapModal,
    insurers,
    alternativeSearchEnabled,
    isAlternativeSearchTypesIncluded
  ])

  const filtersToDisplay = useMemo(() => allFilters.filter((filter) => filter.showingCondition), [allFilters])

  return {
    showMapModal,
    toggleMapModal,
    filtersToDisplay
  }
}

export default useSearchFiltersConfig
